import React, { useState } from "react";
import api from "../../services/api";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const safeMsg = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const newExperience = () => ({
  company: "",
  position: "",
  start_date: "",
  end_date: "",
  description: "",
});

const newEducation = () => ({
  institution: "",
  degree: "",
  field: "",
  graduation_date: "",
});

const newLanguage = () => ({
  language: "",
  proficiency: "",
});

const newCertificate = () => ({
  title: "",
  file_path: "",
});

export default function PublicCandidateApplyPage() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [candidateId, setCandidateId] = useState(null);
  const [cvPdfEndpoint, setCvPdfEndpoint] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    relocate: false,
    expected_salary: "",
    current_salary: "",
    joining_availability_date: "",
    current_job_title: "",
    notes: "",
  });

  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [languageSkills, setLanguageSkills] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const updateArrayRow = (setter, idx, key, value) => {
    setter((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const removeArrayRow = (setter, idx) => {
    setter((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitApplication = async () => {
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        experiences,
        educations,
        language_skills: languageSkills,
        certificates,
      };

      const res = await api.post("/public/candidates/apply", payload);
      const out = res?.data?.data ?? {};

      setCandidateId(out?.id ?? null);
      setCvPdfEndpoint(out?.cv_pdf_endpoint ?? "");
      setSuccess("Application submitted successfully.");
    } catch (e) {
      setError(safeMsg(e, "Failed to submit application."));
    } finally {
      setBusy(false);
    }
  };

  const openGeneratedCv = async () => {
    if (!candidateId) return;
    try {
      const res = await api.get(`/candidates/${candidateId}/cv.pdf`);
      const url = res?.data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setError(safeMsg(e, "Failed to generate CV PDF."));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Candidate Application
          </Typography>
          <Typography color="text.secondary">
            Fill your details and submit your application.
          </Typography>

          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert> : null}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                label="Full Name"
                value={form.full_name}
                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Current Job Title"
                value={form.current_job_title}
                onChange={(e) => setForm((p) => ({ ...p, current_job_title: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Expected Salary"
                value={form.expected_salary}
                onChange={(e) => setForm((p) => ({ ...p, expected_salary: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Current Salary"
                value={form.current_salary}
                onChange={(e) => setForm((p) => ({ ...p, current_salary: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Joining Availability Date"
                value={form.joining_availability_date}
                onChange={(e) => setForm((p) => ({ ...p, joining_availability_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.relocate}
                    onChange={(e) => setForm((p) => ({ ...p, relocate: e.target.checked }))}
                  />
                }
                label="Willing to relocate"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Professional Summary / Notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <ListSection
        title="Experience"
        rows={experiences}
        setRows={setExperiences}
        emptyRow={newExperience}
        fields={[
          { key: "company", label: "Company" },
          { key: "position", label: "Position" },
          { key: "start_date", label: "Start Date", type: "date" },
          { key: "end_date", label: "End Date", type: "date" },
          { key: "description", label: "Description", multiline: true, rows: 3, md: 12 },
        ]}
        updateArrayRow={updateArrayRow}
        removeArrayRow={removeArrayRow}
      />

      <ListSection
        title="Education"
        rows={educations}
        setRows={setEducations}
        emptyRow={newEducation}
        fields={[
          { key: "institution", label: "Institution" },
          { key: "degree", label: "Degree" },
          { key: "field", label: "Field" },
          { key: "graduation_date", label: "Graduation Date", type: "date" },
        ]}
        updateArrayRow={updateArrayRow}
        removeArrayRow={removeArrayRow}
      />

      <ListSection
        title="Language Skills"
        rows={languageSkills}
        setRows={setLanguageSkills}
        emptyRow={newLanguage}
        fields={[
          { key: "language", label: "Language" },
          { key: "proficiency", label: "Proficiency" },
        ]}
        updateArrayRow={updateArrayRow}
        removeArrayRow={removeArrayRow}
      />

      <ListSection
        title="Certificates"
        rows={certificates}
        setRows={setCertificates}
        emptyRow={newCertificate}
        fields={[
          { key: "title", label: "Title" },
          { key: "file_path", label: "File URL / Path (optional)" },
        ]}
        updateArrayRow={updateArrayRow}
        removeArrayRow={removeArrayRow}
      />

      <Card>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={submitApplication}
              disabled={busy}
            >
              {busy ? "Submitting..." : "Submit Application"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={openGeneratedCv}
              disabled={!candidateId}
            >
              Generate CV PDF
            </Button>
          </Stack>

          {cvPdfEndpoint ? (
            <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
              CV endpoint: {cvPdfEndpoint}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Container>
  );
}

function ListSection({
  title,
  rows,
  setRows,
  emptyRow,
  fields,
  updateArrayRow,
  removeArrayRow,
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
          >
            Add
          </Button>
        </Stack>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No records yet.
          </Typography>
        ) : (
          rows.map((row, idx) => (
            <Box key={`${title}-${idx}`} sx={{ mb: 2.5 }}>
              <Grid container spacing={2}>
                {fields.map((f) => (
                  <Grid item xs={12} md={f.md || 6} key={f.key}>
                    <TextField
                      label={f.label}
                      value={row?.[f.key] ?? ""}
                      onChange={(e) => updateArrayRow(setRows, idx, f.key, e.target.value)}
                      type={f.type || "text"}
                      fullWidth
                      multiline={!!f.multiline}
                      rows={f.rows || 1}
                      InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end">
                    <IconButton color="error" onClick={() => removeArrayRow(setRows, idx)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
              {idx !== rows.length - 1 ? <Divider sx={{ mt: 2.5 }} /> : null}
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}

